import { serverSidePagePrepare } from "@lib/hooks"
import { observer } from "mobx-react"
import { useRouter } from "next/router"
import { useEffect, useState } from "react"
import {
  DocumentTemplate,
  Header,
  Page,
  PureLogo,
  useStore,
} from "@features/common"
import { RouterLink } from "@lib/routing"
import { scrollToTop } from "@lib/scroll"
import { IconHamburger } from "@lib/icons"
import {
  DesktopTableOfContents,
  LegalSectionView,
  TermsLogo,
  TouchTableOfContents,
} from "@features/terms"
import { redirect } from "@lib/isomorphic"

export const getServerSideProps = serverSidePagePrepare({
  callback: async ({ Models, ctx }) => {
    try {
      await Models.FaqModel.init()
      await Models.LegalModel.getValentinesDayPageData()
    } catch {
      redirect(`/${Models.GlobalModel.lang}/not-found`, ctx)
    }
  },
})

const ValentinesDayTermsPage = observer(function ValentinesDayTermsPage() {
  const {
    GlobalModel: { openMenu, lang },
    LegalModel: { valentinesDayTermsSections, valentinesDayTermsTitle },
  } = useStore()
  const { asPath } = useRouter()
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [lang])

  useEffect(() => {
    function scrollToAnchor() {
      const hash = asPath.split("#")[1]
      if (hash) {
        const section = document.querySelector(`[data-faq-id=${hash}]`)
        if (section) {
          section.scrollIntoView()
        }
      }
    }
    if (isMounted) {
      setTimeout(() => scrollToAnchor(), 100)
    }
  }, [isMounted, asPath])

  return (
    <Page
      accessLanguages={["en", "fr"]}
      meta={{
        title: valentinesDayTermsTitle,
      }}
      header={
        <Header
          theme="dark"
          logo={
            <RouterLink
              uri="/legal/valentinesday"
              onClick={() => scrollToTop()}
              data-test-id="header__pure_logo"
              aria-label="root"
              className="mt-4 mb-3 tablet-m:mt-[20px] tablet-m:mb-[18px]"
            >
              <div className="shrink-0 flex items-stretch h-4 tablet-m:h-7">
                <PureLogo className="shrink-0 block h-full w-auto tablet:h-full text-white pr-2 tablet-m:pr-4" />
                <TermsLogo />
              </div>
            </RouterLink>
          }
          languageSwitcher={null}
          navigationOpener={
            <button
              onClick={openMenu}
              data-test-id="header__menu_button"
              aria-label="menu"
            >
              <IconHamburger className="w-8 h-8 text-white" />
            </button>
          }
        />
      }
      seoH1={valentinesDayTermsTitle}
    >
      <div className="container pt-3 pb-15 laptop:pt-8 laptop:pb-20 legal-document">
        {valentinesDayTermsSections.length > 1 ? (
          <TouchTableOfContents tableOfContents={valentinesDayTermsSections} />
        ) : null}
        <DocumentTemplate
          sidebar={
            valentinesDayTermsSections.length > 1 ? (
              <DesktopTableOfContents
                tableOfContents={valentinesDayTermsSections}
              />
            ) : null
          }
        >
          <section className="space-y-10" id="scrollContainerElement">
            {valentinesDayTermsSections.map((i) => (
              <LegalSectionView data={i} key={i.id} id={i.id} />
            ))}
          </section>
        </DocumentTemplate>
      </div>
    </Page>
  )
})

export default ValentinesDayTermsPage
